import { IImageLibraryDto } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ImageLibraryService } from "../../../../../services/image-library-service";
import useLoadingPresentation, { LoadingPresentation } from "../../../../../shared/hooks/useLoadingPresentation";
import { IResourceObjectUrlState, useResourceObjectUrl } from "../../../../../shared/hooks/useResourceObjectUrl";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { DeleteModal } from "../../../../common/delete-modal";
import { ResourceImage } from "../../../../common/resource-image";
import KuikaBadge from "../../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaFileInput from "../../../../ui-kit/kuika-file-input/kuika-file-input";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSkeleton, {
  KuikaSkeletonList,
  KuikaSkeletonRegion
} from "../../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSpinnerSmall from "../../../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import { toast } from "../../../../ui-kit/kuika-toast";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallMediumText from "../../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import "./styling-color-picker.scss";

interface IImagePickerProps {
  allImage: IImageLibraryDto[];
  onImageSelect: (image: IImageLibraryDto) => void;
  onFitChange: (fit: string) => void;
  onPositionChange: (position: string) => void;
  selectedImageId?: string;
  selectedFit?: string;
  selectedPosition?: string;
  setBgImageNone?: () => void;
  isLoading?: boolean;
  acceptedFileTypes?: string;
  fileSize?: number;
  getImageLibrary: () => void;
  hidePosition?: boolean;
}

const fitOptions = [
  { value: "contain", label: "Contain" },
  { value: "fit", label: "Fit" },
  { value: "repeat", label: "Repeat" },
  { value: "stretch", label: "Stretch" }
];

/**
 * The side of a library thumbnail (`.kuika_custom-image-picker__image`) and how many the outline
 * draws — two rows of the grid's three — so the placeholder stands where the images will land.
 * The card-grid composition is sized for management cards and would stand several times taller.
 */
const LIBRARY_THUMBNAIL_SIZE: number = 64;
const LIBRARY_PLACEHOLDER_INDEXES: number[] = [0, 1, 2, 3, 4, 5];

const positionOptions = [
  { value: "left", icon: <KuikaSvg name="border-left" type="styling-panel-icons" width={16} height={16} /> },
  { value: "right", icon: <KuikaSvg name="border-right" type="styling-panel-icons" width={16} height={16} /> },
  { value: "bottom", icon: <KuikaSvg name="border-bottom" type="styling-panel-icons" width={16} height={16} /> },
  { value: "top", icon: <KuikaSvg name="border-top" type="styling-panel-icons" width={16} height={16} /> },
  { value: "center", icon: <KuikaSvg name="border-horizontal" type="styling-panel-icons" width={16} height={16} /> }
];

interface ILibraryThumbnailProps {
  image: IImageLibraryDto;
  isSelected: boolean;
  onSelect: (image: IImageLibraryDto) => void;
}

/**
 * One library tile. The image is a CSS background rather than an `<img>`, so it goes through the
 * object-URL hook directly: the library's `resource/{id}/file` URL needs the bearer token, and a
 * `url(...)` the browser fetches on its own carries none.
 */
const LibraryThumbnail = ({ image, isSelected, onSelect }: ILibraryThumbnailProps): JSX.Element => {
  const { src }: IResourceObjectUrlState = useResourceObjectUrl(image.content);
  return (
    <div
      data-testid="fillAreaAiGeneratedImages"
      onClick={(): void => onSelect(image)}
      className={classNames("kuika_custom-image-picker__image", {
        "kuika_custom-image-picker__image--selected": isSelected
      })}
      style={src ? { backgroundImage: `url(${src})` } : undefined}
    ></div>
  );
};

const ImagePicker: React.FC<IImagePickerProps> = ({
  allImage,
  onImageSelect,
  onFitChange,
  onPositionChange,
  selectedImageId,
  selectedFit,
  selectedPosition,
  isLoading,
  acceptedFileTypes,
  fileSize,
  getImageLibrary,
  setBgImageNone,
  hidePosition
}) => {
  const [searchText, setSearchText] = useState("");
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<IImageLibraryDto | null>(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showError, setShowError] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Only the parent's library fetch is a load of what the library lists. `isDataLoading` is this
  // picker's own upload or delete — an action — which keeps its lock on the library below; the
  // refetch an upload ends with runs inside that lock, so it never trades the lock for an outline.
  const libraryPresentation: LoadingPresentation = useLoadingPresentation({
    isLoading: isLoading === true && !isDataLoading,
    hasData: allImage.length > 0
  });
  // While the first load is pending or outlined the library is empty, and "No images uploaded"
  // would claim something the load has not answered yet.
  const isLibraryEmptyStateShown: boolean = libraryPresentation === "content" || libraryPresentation === "refreshing";

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchText(value);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const filteredImages = useCallback(() => {
    if (!debouncedSearchText) {
      return allImage;
    }

    return allImage.filter((image) => image.fileName.toLowerCase().includes(debouncedSearchText.toLowerCase()));
  }, [allImage, debouncedSearchText]);

  const readJsonFile = (file: File, callback: (result: string) => void) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result as string));
    reader.readAsDataURL(file);
  };

  // The dropzone hands over every file of a pick or a drop at once, where antd's
  // Upload fired its onChange once per file. The picker only ever uploads one, so
  // take the first. The file type is not checked here: the dropzone holds a pick and
  // a drop to `accept` alike and hands only the accepted files over.
  const handleFiles = (files: File[]): void => {
    setShowError(false);
    const file: File | undefined = files[0];

    if (!file) {
      return;
    }

    if (file.size !== undefined) {
      const fileSizeInMB: number = file.size / (1024 * 1024);
      if (fileSizeInMB > 10) {
        setShowError(true);
        return;
      }
    }

    readJsonFile(file, (jsonStr: string): void => {
      const data = {} as IImageLibraryDto;
      data.fileName = file.name;
      data.content = jsonStr;
      const allData: IImageLibraryDto[] = [data];
      addImage(allData);
    });
  };

  // A file `accept` turned away never reaches `handleFiles`, so the size notice it would have
  // cleared is cleared here. The toast answers a pick and a drop alike: the zone says so inline
  // only for a drop, and `onReject` does not tell the two apart.
  const handleRejectedFiles = (): void => {
    setShowError(false);
    toast.error("Please select a valid file.");
  };

  const addImage = async (data: IImageLibraryDto[]) => {
    try {
      setIsDataLoading(true);
      const res = await ImageLibraryService.AddOrUpdateResource(data);
      if (res && res.status === 200) {
        const uploadedFileName = data[0]?.fileName;
        const uploadedImage =
          res.data.find((img: IImageLibraryDto) => img.fileName === uploadedFileName) || res.data[res.data.length - 1];
        await (getImageLibrary as () => Promise<void>)();
        onImageSelect(uploadedImage);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      setIsDataLoading(false);
    }
  };

  const triggerDeleteModal = (e: React.MouseEvent, image: IImageLibraryDto) => {
    e.stopPropagation();
    setImageToDelete(image);
    setIsDeleteModalVisible(true);
  };

  const deleteImage = async () => {
    if (!imageToDelete) return;

    setIsDataLoading(true);

    try {
      const res = await ImageLibraryService.Delete(imageToDelete.fileName);
      if (res && res.status === 200) {
        if (selectedImageId === imageToDelete.id && setBgImageNone) {
          setBgImageNone();
        }
        getImageLibrary();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      setIsDataLoading(false);
    }
  };

  const truncateFileName = (fileName: string) => {
    if (fileName.length <= 21) {
      return fileName;
    }
    const firstPart = fileName.substring(0, 9);
    const lastPart = fileName.substring(fileName.length - 9);
    return `${firstPart}...${lastPart}`;
  };

  return (
    <div className="kuika_custom-image-picker">
      {!selectedImageId && showError && (
        <div className="kuika_custom-image-picker__error">
          <KuikaSmallMediumText className="kuika_custom-image-picker__error-text">
            Maximum file size exceeded. (10MB)
          </KuikaSmallMediumText>
        </div>
      )}
      {!selectedImageId && (
        <div className="kuika_custom-image-picker__section">
          <div style={{ width: "100%" }}>
            <KuikaFileInput
              variant="dropzone"
              className="kuika_custom-image-picker__dragger"
              accept={acceptedFileTypes}
              onFiles={handleFiles}
              onReject={handleRejectedFiles}
            >
              <KuikaButton type="secondary" size="sm" uppercase>
                <KuikaSvg name="upload" width={12} height={12} />
                Upload
              </KuikaButton>
            </KuikaFileInput>
          </div>
        </div>
      )}

      {selectedImageId && (
        <div className="kuika_custom-image-picker__section-wrapper" style={{ position: "relative" }}>
          <ResourceImage src={allImage.find((img) => img.id === selectedImageId)?.content} alt="selected" />
          <div
            className="kuika_custom-image-picker__image-clear-wrapper"
            style={{ cursor: "pointer" }}
            onClick={() => setBgImageNone?.()}
          >
            <KuikaButton data-testid="fillAreaGenerateImageModalRemoveImageButton" isIcon type="secondary" size="sm">
              <KuikaSvg name="x" width={16} height={16} />
            </KuikaButton>
          </div>
          <div className="kuika_custom-image-picker__section">
            <KuikaSmallMediumText>Image</KuikaSmallMediumText>
            <KuikaInput value={allImage.find((img) => img.id === selectedImageId)?.fileName || ""} readOnly size="sm" />
          </div>
        </div>
      )}

      <div className="kuika_custom-image-picker__section">
        <KuikaSmallMediumText>Fit</KuikaSmallMediumText>
        <KuikaSelectbox
          options={fitOptions}
          value={selectedFit || undefined}
          onChange={(value) => onFitChange(value?.toString() || "")}
          placeholder="Select"
          size="sm"
        />
      </div>

      {!hidePosition && (
        <div className="kuika_custom-image-picker__section">
          <KuikaSmallMediumText>Position</KuikaSmallMediumText>
          <KuikaTabs options={positionOptions} value={selectedPosition || "left"} onChange={onPositionChange} />
        </div>
      )}

      <div className="kuika_custom-image-picker__section kuika_custom-image-picker__section--library-description">
        <KuikaSmallText>Select image from the library down below</KuikaSmallText>
      </div>

      <div className="kuika_custom-image-picker__section kuika_custom-image-picker__section--library">
        <KuikaSpinnerSmall size="small" spinning={isDataLoading}>
          <div
            className={classNames("kuika_custom-image-picker__library-wrapper", {
              "kuika_custom-image-picker__library-wrapper--list": viewMode === "list"
            })}
          >
            <div
              className={classNames("kuika_custom-image-picker__search-container", {
                "kuika_custom-image-picker__search-container--list": viewMode === "list"
              })}
            >
              <KuikaInput
                data-testid="fillAreaGeneratedImageModalSearchInput"
                placeholder="Search"
                onChange={handleSearchChange}
                value={searchText}
                rightIcon={<KuikaSvg name="search" width={12} height={12} />}
                size="sm"
              />
              <div className="kuika_custom-image-picker__view-toggle">
                <KuikaButton
                  data-testid="fillAreaGeneratedImageModalGridButton"
                  isIcon
                  type={"tertiary"}
                  active={viewMode === "grid"}
                  onClick={() => setViewMode("grid")}
                >
                  <KuikaSvg name="grip-horizontal" type="styling-panel-icons" width={16} height={16} />
                </KuikaButton>
                <KuikaButton
                  data-testid="fillAreaGeneratedImageModalListButton"
                  isIcon
                  type={"tertiary"}
                  active={viewMode === "list"}
                  onClick={() => setViewMode("list")}
                >
                  <KuikaSvg name="list" type="styling-panel-icons" width={16} height={16} />
                </KuikaButton>
              </div>
            </div>
            {libraryPresentation === "skeleton" && (
              <KuikaSkeletonRegion label="Loading images">
                {viewMode === "grid" ? (
                  <div className="kuika_custom-image-picker__library">
                    {LIBRARY_PLACEHOLDER_INDEXES.map((index: number): JSX.Element => (
                      <KuikaSkeleton key={index} width={LIBRARY_THUMBNAIL_SIZE} height={LIBRARY_THUMBNAIL_SIZE} />
                    ))}
                  </div>
                ) : (
                  <KuikaSkeletonList rows={LIBRARY_PLACEHOLDER_INDEXES.length} />
                )}
              </KuikaSkeletonRegion>
            )}
            {filteredImages() && filteredImages().length > 0 ? (
              viewMode === "grid" ? (
                <div className="kuika_custom-image-picker__library">
                  {filteredImages().map((image) => (
                    <KuikaTooltip content={image.fileName} key={image.id}>
                      <div className="kuika_custom-image-picker__image-wrapper">
                        <LibraryThumbnail
                          image={image}
                          isSelected={selectedImageId === image.id}
                          onSelect={onImageSelect}
                        />
                        {selectedImageId === image.id && (
                          <div className="kuika_custom-image-picker__image-check-wrapper">
                            <KuikaBadge type="icon" content={<KuikaSvg name="check" width={12} height={12} />} />
                          </div>
                        )}
                        <div
                          className="kuika_custom-image-picker__delete-button"
                          onClick={(e) => triggerDeleteModal(e, image)}
                        >
                          <KuikaButton data-testid="fillAreaGenerateImageModalDeleteImageButton" isIcon type="tertiary">
                            <KuikaSvg name="trash" width={16} height={16} />
                          </KuikaButton>
                        </div>
                      </div>
                    </KuikaTooltip>
                  ))}
                </div>
              ) : (
                <div className="kuika_custom-image-picker__list">
                  {filteredImages().map((image) => (
                    <div
                      key={image.id}
                      onClick={() => onImageSelect(image)}
                      className={`kuika_custom-image-picker__list-item ${
                        selectedImageId === image.id ? "kuika_custom-image-picker__list-item--selected" : ""
                      }`}
                    >
                      <KuikaSvg
                        name={selectedImageId === image.id ? "check" : "check-empty"}
                        width={16}
                        height={16}
                        className="kuika_custom-image-picker__list-item-check"
                      />
                      <div className="kuika_custom-image-picker__list-item-content">
                        <KuikaSmallText title={image.fileName}>{truncateFileName(image.fileName)}</KuikaSmallText>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              isLibraryEmptyStateShown && (
                <KuikaEmptyState
                  svgName={allImage.length === 0 ? "upload" : "image-in-picture"}
                  svgType={allImage.length === 0 ? "common" : "styling-panel-icons"}
                  customName={allImage.length === 0 ? "No images uploaded" : "No images found"}
                  description={
                    allImage.length === 0
                      ? "Start by uploading images"
                      : "We couldn't find any images matching your search"
                  }
                />
              )
            )}
          </div>
        </KuikaSpinnerSmall>
      </div>

      <DeleteModal
        itemName={imageToDelete?.fileName || ""}
        extraText={"Deleting the image will affect other screens in the app and the action cannot be undone."}
        triggerModal={isDeleteModalVisible}
        closeModal={() => setIsDeleteModalVisible(false)}
        deleteItem={deleteImage}
        deleteType="image"
      />
    </div>
  );
};

export default React.memo(ImagePicker);
